import { useMemo, useRef, useState } from 'react';
import { Upload, Search, Trash2 } from 'lucide-react';
import { useProjectStore } from '../../../state/projectStore';
import { useUiStore } from '../../../state/uiStore';
import { importPhotoFiles } from '../../../photos/importPhotos';
import { Input } from '../../ui/Input';
import { Spinner } from '../../ui/Spinner';
import { EmptyState } from '../../ui/EmptyState';
import { Dialog, DialogContent, DialogFooter } from '../../ui/Dialog';
import { Button } from '../../ui/Button';
import { t } from '../../../i18n';
import type { AssetReference } from '@fotoboek/domain';
import './photos-tab.css';

type SortMode = 'recent' | 'name' | 'unused';
type FilterMode = 'all' | 'used' | 'unused';

export function PhotosTab() {
  const project = useProjectStore((s) => s.project);
  const assetsRuntime = useProjectStore((s) => s.assetsRuntime);
  const addAssets = useProjectStore((s) => s.addAssets);
  const removeAsset = useProjectStore((s) => s.removeAsset);
  const pushToast = useUiStore((s) => s.pushToast);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortMode>('recent');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AssetReference | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const assets = project?.assets ?? [];
    let list = assets.filter((a) =>
      a.originalFileName.toLowerCase().includes(search.toLowerCase()),
    );
    if (filter === 'used') list = list.filter((a) => a.useCount > 0);
    if (filter === 'unused') list = list.filter((a) => a.useCount === 0);
    list = [...list];
    if (sort === 'name') list.sort((a, b) => a.originalFileName.localeCompare(b.originalFileName));
    else if (sort === 'unused') list.sort((a, b) => a.useCount - b.useCount);
    else list.sort((a, b) => (a.importedAt < b.importedAt ? 1 : -1));
    return list;
  }, [project?.assets, search, filter, sort]);

  const handleFiles = async (files: File[]) => {
    if (!project || files.length === 0) return;
    setImporting(true);
    const result = await importPhotoFiles(project.id, files);
    if (result.imported.length > 0) {
      addAssets(
        result.imported.map((p) => p.asset),
        Object.fromEntries(
          result.imported.map((p) => [
            p.asset.id,
            {
              objectUrl: p.objectUrl,
              thumbnailUrl: p.thumbnailUrl,
              widthPx: p.asset.widthPx,
              heightPx: p.asset.heightPx,
            },
          ]),
        ),
      );
    }
    if (result.errors.length > 0) {
      pushToast({
        kind: 'error',
        message: `${result.errors.length} bestand(en) konden niet worden geïmporteerd.`,
      });
    }
    setImporting(false);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    await removeAsset(pendingDelete.id);
    setPendingDelete(null);
  };

  return (
    <div className="left-panel-tab-body photos-tab">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="visually-hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) void handleFiles(files);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        className={dragOver ? 'photos-drop-zone drag-over' : 'photos-drop-zone'}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const files = Array.from(e.dataTransfer.files ?? []);
          if (files.length) void handleFiles(files);
        }}
      >
        {importing ? <Spinner size={18} /> : <Upload size={18} />}
        <span>
          {importing ? t('editor.photos.importing', { count: 1 }) : t('editor.photos.dropHint')}
        </span>
      </button>

      <div className="photos-toolbar">
        <div className="photos-search">
          <Search size={14} />
          <Input
            placeholder={t('editor.photos.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortMode)}
          className="photos-select"
        >
          <option value="recent">{t('editor.photos.sortRecent')}</option>
          <option value="name">{t('editor.photos.sortName')}</option>
          <option value="unused">{t('editor.photos.sortUnused')}</option>
        </select>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as FilterMode)}
          className="photos-select"
        >
          <option value="all">{t('common.search')}</option>
          <option value="used">{t('editor.photos.filterUsed')}</option>
          <option value="unused">{t('editor.photos.filterUnused')}</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title={t('editor.photos.empty')} />
      ) : (
        <div className="photos-grid">
          {filtered.map((asset) => {
            const runtime = assetsRuntime[asset.id];
            return (
              <div key={asset.id} className="photo-thumb">
                <img
                  src={runtime?.thumbnailUrl ?? runtime?.objectUrl}
                  alt={asset.originalFileName}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/x-fotoboek-asset-id', asset.id)}
                />
                {asset.useCount > 0 ? (
                  <span className="photo-thumb-badge">{asset.useCount}×</span>
                ) : null}
                <button
                  className="photo-thumb-delete"
                  title={t('common.delete')}
                  onClick={() => setPendingDelete(asset)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        {pendingDelete ? (
          <DialogContent title={t('editor.photos.deleteConfirmTitle')}>
            <p>
              {pendingDelete.useCount > 0
                ? t('editor.photos.deleteConfirmBodyUsed', { count: pendingDelete.useCount })
                : t('editor.photos.deleteConfirmBodyUnused')}
            </p>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setPendingDelete(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={confirmDelete}>
                {t('common.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
